import { AddOutlined, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { InputError } from "src/grading/components/InputError";
import OrganizationLayout from "src/grading/layout/OrganizationLayout/OrganizationLayout";
import {
  Organization,
  useLazyGetOrganizationsQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import CreateOrgModal from "../CreateOrgModal/CreateOrgModal";
import OrganizationCard from "../OrgCard/OrgCard";
import useStyles from "./OrgList.styles";

const Organizations = () => {
  const classes = useStyles();

  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const [getOrganizations, { isLoading }] = useLazyGetOrganizationsQuery();

  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      const { organizations: o } = await getOrganizations({}).unwrap();
      setOrganizations(o);
    };

    fetchOrganizations();
  }, [getOrganizations]);

  const fetchOrganizations = async () => {
    const { organizations: o } = await getOrganizations({}).unwrap();
    setOrganizations(o);
  };

  const searchValueRef = useRef<string>("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debounceSearch = useCallback(
    (searchValue: string) => {
      searchValueRef.current = searchValue;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(async () => {
        const { organizations: o } = await getOrganizations({
          search: searchValueRef.current,
        }).unwrap();
        setOrganizations(o);
      }, 500);
    },
    [getOrganizations],
  );

  const formik = useFormik({
    initialValues: { search: "" },
    onSubmit: async (values) => {
      console.log("values", values);
    },
  });

  const { touched, values, errors, setFieldValue } = formik;

  return isLoading ? (
    <Spinner
      animation="border"
      style={{ left: "50%", top: "50%", position: "relative" }}
    />
  ) : (
    <OrganizationLayout>
      {/* PAGE TITLE */}
      <Grid item xs={12} mt={1} className={classes.headerContainer}>
        <Typography variant="h2">Organizations</Typography>
        <Button
          variant="contained"
          size="small"
          className={classes.headerButton}
          onClick={() => setCreateOrgModalOpen(true)}
        >
          <AddOutlined />
          Create Org
        </Button>
      </Grid>

      {/* FILTERS */}
      <Grid item container className={classes.filtersContainer}>
        <TextField
          label="Search by org name"
          name="search"
          type="text"
          value={values.search}
          onChange={(e) => {
            setFieldValue("search", e.target.value);
            debounceSearch(e.target.value);
          }}
          style={{ paddingBottom: "20px" }}
          InputProps={{
            className: "text-field",
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          placeholder="Search by org name"
          error={InputError("search", touched, errors).error}
          helperText={InputError("search", touched, errors).helperText}
        />

        <Grid item sx={{ flex: 1 }}>
          <Box display="flex" justifyContent="flex-end" gap={1} />
        </Grid>
      </Grid>

      {/* ORGANIZATIONS LIST */}

      <Grid container mt={1}>
        {organizations.map((org) => (
          <OrganizationCard key={org.id} organization={org} />
        ))}
      </Grid>

      <CreateOrgModal
        onClose={() => setCreateOrgModalOpen(false)}
        open={createOrgModalOpen}
        fetchOrganizations={fetchOrganizations}
      />
    </OrganizationLayout>
  );
};

export default Organizations;
